import React from 'react'
import { OfferWrapper, Trivia } from 'generated/api'
import { ID } from 'lib'
import { Pending } from 'components/patterns/suspense/internals/types'
import { Hidden, Skeleton, Text, View } from 'reshaped/bundle'

export const getTriviaFromOffer = (offer: OfferWrapper): Array<JSX.Element> =>
	offer.trivia
		.sort((a, b) => (b.importance || 0) - (a.importance || 0))
		.map((trivia, index) => {
			if (index === 0) {
				return <MostImportantTriviaItem key={trivia.key || `trivia-${index}`} trivia={trivia} />
			} else {
				return <TriviaItem key={trivia.key || `trivia-${index}`} trivia={trivia} />
			}
		})

export const getSkeletonTrivia = (): JSX.Element[] => {
	return [1, 2, 3, 4].map(i => <TriviaItem key={i} pending={true} />)
}

interface TriviaProps {
	trivia: Trivia
}

const MostImportantTriviaItem: React.FC<React.PropsWithChildren<Pending<TriviaProps>>> = props => {
	let value, title, key

	if (props.pending) {
		key = ID()
		title = <Skeleton width="100px" height="24px" />
		value = <Skeleton width="80px" height={{ s: '24px', xl: '32px' }} />
	} else {
		const trivia = props.trivia
		value = trivia.value
		key = trivia.key
		title = trivia.title
	}

	return (
		<View.Item key={key} grow>
			<View direction="column" align="start">
				<Text variant="body-2" color="neutral-faded" attributes={{ style: { whiteSpace: 'nowrap' } }}>
					{title}
				</Text>
				<View direction="row" justify={{ s: 'start', xl: 'start' }}>
					<Text variant="featured-2" weight="bold">
						{value}
					</Text>
				</View>
			</View>
		</View.Item>
	)
}

const TriviaItem: React.FC<React.PropsWithChildren<Pending<TriviaProps>>> = props => {
	let value, title, key

	if (props.pending) {
		key = ID()
		title = <Skeleton width="100px" height="24px" />
		value = <Skeleton width="80px" height={{ s: '24px', xl: '32px' }} />
	} else {
		const trivia = props.trivia
		value = trivia.value
		key = trivia.key
		title = trivia.title
	}

	return (
		<View.Item key={key} grow>
			<View direction={{ s: 'row', xl: 'column' }} align="start">
				<Text variant="body-2" color="neutral-faded" attributes={{ style: { whiteSpace: 'nowrap' } }}>
					{title}
				</Text>
				<View.Item grow />
				<View direction="row" justify={{ s: 'end', xl: 'start' }}>
					<Hidden hide={{ s: false, xl: true }}>
						<Text variant="body-2" weight="bold">
							{value}
						</Text>
					</Hidden>
					<Hidden hide={{ s: true, xl: false }}>
						<Text variant="featured-2" weight="bold">
							{value}
						</Text>
					</Hidden>
				</View>
			</View>
		</View.Item>
	)
}
